import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { Navigate, Link } from 'react-router-dom';
import { Card } from 'react-bootstrap';
import ClipLoader from 'react-spinners/ClipLoader';
import { useFormatMessage, useFormatDate } from 'hooks';
import Table from 'components/Table';
import paths from 'pages/Router/paths';
import ConfirmationModal from 'components/ConfirmationModal';
import NavSearch from 'components/Layout/NavSearch';
import { Timestamp } from 'firebase/firestore';
import {
  fetchSlideShows,
  deleteSlideShow,
  clearSlideShowsData,
} from '../../state/actions/slideShows';
import { sortDate } from '../../utils';

const SlideShows = () => {
  const { slideShowList, isAdmin, error, loading, deleted } = useSelector(
    (state) => ({
      slideShowList: state.slideShows.data,
      isAdmin: state.auth.userData.isAdmin,
      error: state.slideShows.error,
      loading: state.slideShows.loading,
      deleted: state.slideShows.deleted,
    }),
    shallowEqual,
  );
  const [deleteModal, setDeleteModal] = useState({
    catId: null,
    isOpen: false,
  });

  const dispatch = useDispatch();

  const [search, setSearch] = useState();

  useEffect(() => {
    if (isAdmin) {
      dispatch(fetchSlideShows(true));
    }

    return () => dispatch(clearSlideShowsData());
  }, [dispatch, isAdmin]);

  useEffect(() => {
    if (deleted && !loading) {
      setDeleteModal((prevState) => ({
        catId: null,
        isOpen: false,
      }));
    }
  }, [deleted, loading]);

  const redirect = !isAdmin && <Navigate to={paths.ROOT} />;

  const onRemoveButtonClickHandler = (catId) => {
    setDeleteModal((prevState) => ({
      catId,
      isOpen: !prevState.isOpen,
    }));
  };

  const onCloseModalHandler = () => {
    setDeleteModal({ catId: null, isOpen: false });
  };

  const onDeleteLevelHandler = () => {
    dispatch(deleteSlideShow(deleteModal.catId));
  };

  const columns = [
    {
      Header: 'Identifier',
      accessor: 'identifier',
    },
    {
      Header: 'Name',
      accessor: 'name',
    },
    {
      Header: 'Created At',
      accessor: 'createdAt',
      sortType: sortDate,
      Cell: ({ row }) => (
        <small className="has-text-grey is-abbr-like">
          {row.original.createdAt instanceof Timestamp &&
            useFormatDate(row.original.createdAt.toDate(), {
              weekday: 'short',
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            })}
        </small>
      ),
    },
    {
      Header: '',
      id: 'actions',
      accessor: 'actions',
      Cell: ({ row }) => (
        <div className="button-actions is-right">
          <Link
            to={`/slide-show/${row.original.id}`}
            className="btn btn-sm btn-primary"
          >
            <i className="feather icon-edit" />
          </Link>

          <button
            type="button"
            className="btn btn-sm btn-danger"
            onClick={() => onRemoveButtonClickHandler(row.original.id)}
          >
            <i className="feather icon-trash" />
          </button>
        </div>
      ),
      disableSortBy: true,
    },
  ];

  const data = search
    ? slideShowList.filter((el) => {
        const clonedElem = { name: el.name };
        return Object.values(clonedElem).some((field) =>
          field.toString().toLowerCase().includes(search.toLowerCase()),
        );
      })
    : slideShowList;

  const deleteMessage = useFormatMessage('Levels.delete');

  const confirmMessage = useFormatMessage('Levels.confirm');

  const permDeleteMessage = useFormatMessage('Levels.permDelete');

  const cancelMessage = useFormatMessage('Levels.cancel');

  return (
    <>
      {redirect}
      {deleteModal.isOpen && (
        <ConfirmationModal
          isActive={deleteModal.isOpen}
          isLoading={loading}
          confirmButtonMessage={deleteMessage}
          title={confirmMessage}
          body={permDeleteMessage}
          cancelButtonMessage={cancelMessage}
          onConfirmation={onDeleteLevelHandler}
          onCancel={onCloseModalHandler}
        />
      )}
      <Card>
        <Card.Header>
          <div className="d-flex flex-row align-items-center">
            <Card.Title as="h5">Manage Slide Shows</Card.Title>
            <NavSearch handleChange={setSearch} />
            <Link to={paths.ADD_SLIDE} className="btn btn-primary ms-auto">
              Add new
            </Link>
          </div>
        </Card.Header>
        <Card.Body>
          <div className="b-table">
            {loading ? <ClipLoader /> : <Table columns={columns} data={data} />}
            {error && 'Show error'}
          </div>
        </Card.Body>
      </Card>
    </>
  );
};

export default SlideShows;
